import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { FaClock, FaEdit, FaTrash, FaPlus, FaRoute, FaSearch, FaThLarge, FaList, FaMapMarkerAlt } from "react-icons/fa";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";
import { useGoogleMaps } from "../../context/GoogleMapsContext";

const RentalPackages = () => {
  const { isLoaded: isGoogleLoaded } = useGoogleMaps();

  const [packages, setPackages] = useState([]);
  const [cities, setCities] = useState([]);
  const [cabTypes, setCabTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    serviceCityNames: [],
    newServiceCity: "",
    citySpecificPrices: {}, // New structure: { cityName: { cabTypeName: price } }
    citySpecificExtraFarePerHour: {}, // New structure: { cityName: { cabTypeName: extraFarePerHour } }
    cityCoordinates: {}, // New: { cityName: { latitude, longitude } }
    // Legacy support
    cabTypePrices: {},
  });

  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("table");
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [selectedCityCoordinates, setSelectedCityCoordinates] = useState(null);
  const cityInputRef = useRef(null);

  useEffect(() => {
    fetchPackages();
    fetchCities();
    fetchCabTypes();
  }, []);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/rental-package`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setPackages(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      toast.error("Failed to fetch rental packages");
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await axios.get(`${API_URL}/city`);
      if (response.data.success) {
        setCities(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  const fetchCabTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/cab`);
      if (response.data.success) {
        setCabTypes(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching cab types:", error);
    }
  };

  // Get city suggestions from Google Places
  const getCitySuggestions = async (input) => {
    if (!input || input.length < 2 || !isGoogleLoaded) return;

    try {
      const service = new window.google.maps.places.AutocompleteService();
      const request = {
        input: input,
        types: ['locality', 'administrative_area_level_3'], // Cities and administrative regions
        componentRestrictions: { country: ['in'] }, // Restrict to India
      };

      const predictions = await new Promise((resolve) => {
        service.getPlacePredictions(request, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            resolve(predictions || []);
          } else {
            resolve([]);
          }
        });
      });

      setCitySuggestions(predictions);
    } catch (error) {
      console.error('Error fetching city suggestions:', error);
      setCitySuggestions([]);
    }
  };

  // Handle city suggestion click and get coordinates
  const handleCitySuggestionClick = (suggestion) => {
    const cityName = suggestion.description;

    // Get coordinates from Google Geocoding API
    if (isGoogleLoaded) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
        if (status === window.google.maps.GeocoderStatus.OK && results[0]) {
          const location = results[0].geometry.location;
          setSelectedCityCoordinates({
            latitude: location.lat(),
            longitude: location.lng(),
            cityName: cityName,
          });
          console.log("📍 City Coordinates:", { cityName, latitude: location.lat(), longitude: location.lng() });
        } else {
          console.warn("Could not get coordinates for city:", cityName);
          setSelectedCityCoordinates({ cityName: cityName, latitude: null, longitude: null });
        }
      });
    }

    setFormData((prev) => ({
      ...prev,
      newServiceCity: cityName,
    }));
    setCitySuggestions([]);
    setShowCitySuggestions(false);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cityInputRef.current && !cityInputRef.current.contains(event.target)) {
        setShowCitySuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Try to get token from different storage locations
      let token = localStorage.getItem("token");

      if (!token) {
        // Try to get token from user object
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const user = JSON.parse(userStr);
          token = user.token;
        }
      }

      if (!token) {
        toast.error("No authentication token found. Please log in again.");
        return;
      }

      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      // Convert city-specific prices to new format with coordinates
      const serviceCitiesWithPrices = formData.serviceCityNames.map(
        (cityName) => {
          const cityPrices = {};
          const cityExtraFarePerHour = {};
          const citySpecificPricing =
            formData.citySpecificPrices[cityName] || {};
          const citySpecificExtraFare =
            formData.citySpecificExtraFarePerHour[cityName] || {};
          const cityCoords = formData.cityCoordinates[cityName] || {};

          // Convert cab type prices and extra fare per hour for this city
          cabTypes.forEach((cabType) => {
            const price =
              citySpecificPricing[cabType.name] ||
              citySpecificPricing[cabType._id] ||
              0;
            const extraFarePerHour =
              citySpecificExtraFare[cabType.name] ||
              citySpecificExtraFare[cabType._id] ||
              0;

            if (price > 0) {
              cityPrices[cabType.name] = parseInt(price);
            }
            if (extraFarePerHour > 0) {
              cityExtraFarePerHour[cabType.name] = parseInt(extraFarePerHour);
            }
          });

          const serviceCity = {
            cityName,
            cabPrices: cityPrices,
            extraFarePerHour: cityExtraFarePerHour,
          };

          // Add coordinates if available
          if (cityCoords.latitude && cityCoords.longitude) {
            serviceCity.latitude = Number(cityCoords.latitude);
            serviceCity.longitude = Number(cityCoords.longitude);
          }

          return serviceCity;
        },
      );

      const submitData = {
        name: formData.name,
        serviceCities: serviceCitiesWithPrices,
      };

      if (editingPackage) {
        await axios.put(
          `${API_URL}/rental-package/${editingPackage._id}`,
          submitData,
          config,
        );
        toast.success("Package updated successfully");
      } else {
        await axios.post(`${API_URL}/rental-package`, submitData, config);
        toast.success("Package created successfully");
      }

      fetchPackages();
      resetForm();
    } catch (error) {
      console.error("Error saving package:", error);
      toast.error(error.response?.data?.message || "Failed to save package");
    }
  };

  const handleEdit = (pkg) => {
    setEditingPackage(pkg);

    // Handle new structure vs legacy structure
    let serviceCityNames = [];
    let citySpecificPrices = {};
    let citySpecificExtraFarePerHour = {};
    let cityCoordinates = {};

    if (pkg.serviceCities && pkg.serviceCities.length > 0) {
      if (pkg.serviceCities[0].cityName) {
        // New structure
        serviceCityNames = pkg.serviceCities.map((sc) => sc.cityName);

        // Convert city-specific prices, extra fare per hour, and coordinates
        pkg.serviceCities.forEach((sc) => {
          if (sc.cabPrices) {
            citySpecificPrices[sc.cityName] = {};
            Object.entries(sc.cabPrices).forEach(([cabTypeName, price]) => {
              citySpecificPrices[sc.cityName][cabTypeName] = price;
            });
          }

          if (sc.extraFarePerHour) {
            citySpecificExtraFarePerHour[sc.cityName] = {};
            Object.entries(sc.extraFarePerHour).forEach(
              ([cabTypeName, extraFare]) => {
                citySpecificExtraFarePerHour[sc.cityName][cabTypeName] =
                  extraFare;
              },
            );
          }

          // Load coordinates if available
          if (sc.latitude && sc.longitude) {
            cityCoordinates[sc.cityName] = {
              latitude: sc.latitude,
              longitude: sc.longitude,
            };
          }
        });
      } else {
        // Legacy structure
        serviceCityNames = pkg.serviceCities.map((city) => city.name || city);
      }
    } else if (pkg.availableCities && pkg.availableCities.length > 0) {
      // Legacy available cities
      serviceCityNames = pkg.availableCities.map((city) => city.name || city);
    }

    // Legacy cab type prices
    const cabTypePricesObj = {};
    if (pkg.cabTypePrices) {
      pkg.cabTypePrices.forEach((ctp) => {
        cabTypePricesObj[ctp.cabTypeId._id || ctp.cabTypeId] = ctp.price;
      });
    }

    setFormData({
      name: pkg.name,
      serviceCityNames: serviceCityNames,
      newServiceCity: "",
      citySpecificPrices: citySpecificPrices,
      citySpecificExtraFarePerHour: citySpecificExtraFarePerHour,
      cityCoordinates: cityCoordinates,
      cabTypePrices: cabTypePricesObj,
    });
    setShowModal(true);
  };

  const handleDelete = async (packageId) => {
    if (window.confirm("Are you sure you want to delete this package?")) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${API_URL}/rental-package/${packageId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Package deleted successfully");
        fetchPackages();
      } catch (error) {
        console.error("Error deleting package:", error);
        toast.error("Failed to delete package");
      }
    }
  };

  const resetForm = () => {
    // Initialize cab type prices for new package
    const cabTypePricesObj = {};
    cabTypes.forEach((cabType) => {
      cabTypePricesObj[cabType._id] = 0;
    });

    setFormData({
      name: "",
      serviceCityNames: [],
      newServiceCity: "",
      citySpecificPrices: {},
      citySpecificExtraFarePerHour: {},
      cityCoordinates: {},
      cabTypePrices: cabTypePricesObj,
    });
    setCitySuggestions([]);
    setShowCitySuggestions(false);
    setSelectedCityCoordinates(null);
    setEditingPackage(null);
    setShowModal(false);
  };

  const handleAddServiceCity = () => {
    if (formData.newServiceCity.trim()) {
      const cityName = formData.newServiceCity.trim();

      // Check if coordinates are available
      if (!selectedCityCoordinates || !selectedCityCoordinates.latitude || !selectedCityCoordinates.longitude) {
        toast.error("Please wait for coordinates to load, or coordinates could not be found for this city");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        serviceCityNames: [
          ...prev.serviceCityNames,
          cityName,
        ],
        cityCoordinates: {
          ...prev.cityCoordinates,
          [cityName]: {
            latitude: selectedCityCoordinates.latitude,
            longitude: selectedCityCoordinates.longitude,
          },
        },
        newServiceCity: "",
      }));

      setSelectedCityCoordinates(null);
    }
  };

  const handleRemoveServiceCity = (index) => {
    const cityToRemove = formData.serviceCityNames[index];
    setFormData((prev) => {
      const newCitySpecificPrices = { ...prev.citySpecificPrices };
      const newCitySpecificExtraFarePerHour = {
        ...prev.citySpecificExtraFarePerHour,
      };
      const newCityCoordinates = { ...prev.cityCoordinates };

      delete newCitySpecificPrices[cityToRemove];
      delete newCitySpecificExtraFarePerHour[cityToRemove];
      delete newCityCoordinates[cityToRemove];

      return {
        ...prev,
        serviceCityNames: prev.serviceCityNames.filter((_, i) => i !== index),
        citySpecificPrices: newCitySpecificPrices,
        citySpecificExtraFarePerHour: newCitySpecificExtraFarePerHour,
        cityCoordinates: newCityCoordinates,
      };
    });
  };

  const handleCitySpecificPriceChange = (cityName, cabTypeName, price) => {
    setFormData((prev) => ({
      ...prev,
      citySpecificPrices: {
        ...prev.citySpecificPrices,
        [cityName]: {
          ...prev.citySpecificPrices[cityName],
          [cabTypeName]: parseInt(price) || 0,
        },
      },
    }));
  };

  const handleCitySpecificExtraFarePerHourChange = (
    cityName,
    cabTypeName,
    extraFarePerHour,
  ) => {
    setFormData((prev) => ({
      ...prev,
      citySpecificExtraFarePerHour: {
        ...prev.citySpecificExtraFarePerHour,
        [cityName]: {
          ...prev.citySpecificExtraFarePerHour[cityName],
          [cabTypeName]: parseInt(extraFarePerHour) || 0,
        },
      },
    }));
  };

  const handleCabTypePriceChange = (cabTypeId, price) => {
    setFormData((prev) => ({
      ...prev,
      cabTypePrices: {
        ...prev.cabTypePrices,
        [cabTypeId]: parseInt(price) || 0,
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <FaClock className="mr-3 text-blue-600" />
          Rental Packages Management
        </h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
        >
          <FaPlus className="mr-2" />
          Add Package
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 w-full md:w-1/2">
          <div className="relative flex-1">
            <input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search packages or cities..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setViewMode("table")}
              className={`px-3 py-2 rounded ${viewMode === "table" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode("cards")}
              className={`px-3 py-2 rounded ${viewMode === "cards" ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"}`}
            >
              Cards
            </button>
          </div>
        </div>
        <div className="text-sm text-gray-500">Showing {packages.length} packages</div>
      </div>

      {/* Card Grid (mobile / cards view) */}
      <div className={`${viewMode === 'cards' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4' : 'md:hidden grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4'}`}>
        {packages.filter(p => {
          const q = searchQuery.trim().toLowerCase();
          if (!q) return true;
          const name = (p.name || '').toLowerCase();
          const cities = (p.serviceCities || p.availableCities || []).map(c => (c.cityName || c.name || c).toString().toLowerCase()).join(' ');
          return name.includes(q) || cities.includes(q);
        }).map((pkg) => (
          <div key={pkg._id} className="bg-white p-4 rounded-lg shadow hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-800">{pkg.name}</div>
                <div className="mt-2 text-xs text-gray-600">
                  {pkg.serviceCities?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {pkg.serviceCities.slice(0,5).map((c, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full">{c.cityName || c.name || c}</span>
                      ))}
                      {pkg.serviceCities.length > 5 && <span className="text-xs text-gray-400">+{pkg.serviceCities.length - 5} more</span>}
                    </div>
                  ) : pkg.availableCities?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {pkg.availableCities.slice(0,5).map((c, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-full">{c.name || c}</span>
                      ))}
                      {pkg.availableCities.length > 5 && <span className="text-xs text-gray-400">+{pkg.availableCities.length - 5} more</span>}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">No cities configured</span>
                  )}
                </div>
              </div>
              <div className="ml-3 flex flex-col items-end">
                <div className="text-sm font-medium text-green-600">{pkg.serviceCities?.length > 0 || pkg.availableCities?.length > 0 ? 'Configured' : 'No Config'}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => handleEdit(pkg)} className="text-blue-600 hover:text-blue-900"><FaEdit /></button>
                  <button onClick={() => handleDelete(pkg._id)} className="text-red-600 hover:text-red-900"><FaTrash /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Packages List */}
      <div className={`${viewMode === 'cards' ? 'hidden md:block' : ''} bg-white rounded-lg shadow overflow-hidden`}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Package Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <div className="flex items-center">
                  <FaClock className="mr-1 text-orange-500" />
                  Extra Fare/Hour Details
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Service Cities
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                City-wise Pricing
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {packages.filter(p => {
              const q = searchQuery.trim().toLowerCase();
              if (!q) return true;
              const name = (p.name || '').toLowerCase();
              const cities = (p.serviceCities || p.availableCities || []).map(c => (c.cityName || c.name || c).toString().toLowerCase()).join(' ');
              return name.includes(q) || cities.includes(q);
            }).map((pkg) => (
              <tr key={pkg._id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {pkg.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {pkg.serviceCities?.length > 0 ? (
                    <div className="space-y-1">
                      {pkg.serviceCities.slice(0, 2).map((city, index) => (
                        <div key={city.cityName || index} className="text-xs">
                          <span className="font-medium text-blue-600">{city.cityName}:</span>
                          {city.extraFarePerHour && Object.keys(city.extraFarePerHour).length > 0 ? (
                            <div className="ml-2">
                              {Object.entries(city.extraFarePerHour).map(([cabType, fare]) => (
                                <div key={cabType} className="text-gray-600">
                                  {cabType}: ₹{fare}/Hour
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400 ml-2">No extra fare set</span>
                          )}
                        </div>
                      ))}
                      {pkg.serviceCities.length > 2 && (
                        <div className="text-xs text-gray-400">
                          +{pkg.serviceCities.length - 2} more cities
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">No cities configured</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="flex flex-wrap gap-1 max-w-xs">
                    {pkg.serviceCities?.length > 0 ? (
                      pkg.serviceCities.slice(0, 3).map((city, index) => (
                        <span
                          key={city.cityName || city._id || index}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                        >
                          {city.cityName || city.name || city}
                        </span>
                      ))
                    ) : pkg.availableCities?.length > 0 ? (
                      pkg.availableCities.slice(0, 3).map((city) => (
                        <span
                          key={city._id || city}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                        >
                          {city.name || city}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400">No cities</span>
                    )}
                    {(pkg.serviceCities?.length > 3 ||
                      pkg.availableCities?.length > 3) && (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                        +
                        {(pkg.serviceCities?.length ||
                          pkg.availableCities?.length ||
                          0) - 3}{" "}
                        more
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  <div className="space-y-2">
                    {pkg.serviceCities?.length > 0 &&
                    pkg.serviceCities[0]?.cabPrices ? (
                      // New structure - show city-specific pricing
                      pkg.serviceCities.map((serviceCity, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded p-2 bg-gray-50"
                        >
                          <div className="text-xs font-medium text-gray-700 mb-1">
                            {serviceCity.cityName}
                          </div>
                          <div className="space-y-1">
                            {Object.entries(serviceCity.cabPrices || {}).map(
                              ([cabType, price]) => (
                                <div
                                  key={cabType}
                                  className="flex justify-between items-center text-xs"
                                >
                                  <span className="text-gray-600">
                                    {cabType}
                                  </span>
                                  <span className="font-medium text-green-600">
                                    ₹{price}
                                  </span>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      ))
                    ) : pkg.cabTypePrices && pkg.cabTypePrices.length > 0 ? (
                      // Legacy structure
                      pkg.cabTypePrices.map((ctp) => (
                        <div
                          key={ctp.cabTypeId._id || ctp.cabTypeId}
                          className="flex justify-between items-center bg-gray-50 p-2 rounded text-xs"
                        >
                          <span className="text-gray-800">
                            {ctp.cabTypeId.name || "Unknown Cab Type"}
                          </span>
                          <span className="font-medium text-green-600">
                            ₹{ctp.price}
                          </span>
                        </div>
                      ))
                    ) : (
                      <span className="text-xs text-gray-500">
                        No prices set
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(pkg)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDelete(pkg._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingPackage ? "Edit Package" : "Add New Package"}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Package Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="e.g., 4 Hours / 40 KM"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Cities (where package is available) <FaMapMarkerAlt className="inline text-blue-500 ml-1" />
                  </label>
                  <div className="space-y-3">
                    {/* Add new service city with Google Places autocomplete */}
                    <div ref={cityInputRef} className="relative flex gap-2">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={formData.newServiceCity || ""}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              newServiceCity: e.target.value,
                            });
                            if (isGoogleLoaded) {
                              getCitySuggestions(e.target.value);
                              setShowCitySuggestions(e.target.value.length >= 2);
                            }
                          }}
                          onFocus={() => setShowCitySuggestions(formData.newServiceCity.length >= 2)}
                          className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          placeholder="Search city..."
                          disabled={!isGoogleLoaded}
                        />
                        {formData.newServiceCity && (
                          <div className="absolute right-2 top-2 text-xs">
                            {selectedCityCoordinates && selectedCityCoordinates.latitude ? (
                              <span className="text-green-600 flex items-center">
                                <FaMapMarkerAlt className="mr-1" /> Coordinates ready
                              </span>
                            ) : (
                              <span className="text-gray-500">Select a city...</span>
                            )}
                          </div>
                        )}
                        {showCitySuggestions && citySuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                            {citySuggestions.map((suggestion, idx) => (
                              <div
                                key={idx}
                                onClick={() => handleCitySuggestionClick(suggestion)}
                                className="px-3 py-2 hover:bg-blue-100 cursor-pointer text-sm text-gray-700"
                              >
                                <FaMapMarkerAlt className="inline mr-2 text-gray-400" />
                                {suggestion.description}
                              </div>
                            ))}
                          </div>
                        )}
                        {!isGoogleLoaded && (
                          <p className="text-xs text-gray-500 mt-1">Loading Google Places...</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleAddServiceCity}
                        disabled={!formData.newServiceCity.trim() || !selectedCityCoordinates?.latitude}
                        className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>

                    {/* List of added service cities with coordinates */}
                    <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-3">
                      {formData.serviceCityNames &&
                      formData.serviceCityNames.length > 0 ? (
                        formData.serviceCityNames.map((cityName, index) => {
                          const hasCoordinates = formData.cityCoordinates[cityName] &&
                                               formData.cityCoordinates[cityName].latitude &&
                                               formData.cityCoordinates[cityName].longitude;
                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between mb-2 p-2 bg-gray-50 rounded"
                            >
                              <div className="flex-1">
                                <div className="text-sm text-gray-700 flex items-center">
                                  {cityName}
                                  {hasCoordinates && (
                                    <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex items-center">
                                      <FaMapMarkerAlt className="mr-1" />
                                      {formData.cityCoordinates[cityName].latitude.toFixed(4)}, {formData.cityCoordinates[cityName].longitude.toFixed(4)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => handleRemoveServiceCity(index)}
                                className="text-red-600 hover:text-red-800 text-sm ml-2"
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No service cities added yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* City-Specific Cab Pricing */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Set Cab Prices for Each Service City
                  </label>
                  <div className="space-y-4 max-h-96 overflow-y-auto border border-gray-300 rounded-md p-3">
                    {formData.serviceCityNames.length > 0 ? (
                      formData.serviceCityNames.map((cityName, cityIndex) => (
                        <div
                          key={cityIndex}
                          className="bg-gray-50 p-4 rounded-lg border"
                        >
                          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                            <FaRoute className="mr-2 text-blue-600" />
                            {cityName} - Cab Pricing
                          </h4>
                          <div className="grid grid-cols-1 gap-3">
                            {cabTypes.map((cabType) => (
                              <div
                                key={cabType._id}
                                className="p-3 bg-white border rounded-lg"
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex-1">
                                    <span className="text-sm font-medium text-gray-700">
                                      {cabType.name}
                                    </span>
                                    <span className="text-xs text-gray-500 block">
                                      {cabType.acType} -{" "}
                                      {cabType.seatingCapacity} seats
                                    </span>
                                  </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  {/* Package Price */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Package Price (₹)
                                    </label>
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-gray-600">
                                        ₹
                                      </span>
                                      <input
                                        type="number"
                                        value={
                                          formData.citySpecificPrices[
                                            cityName
                                          ]?.[cabType.name] ||
                                          formData.citySpecificPrices[
                                            cityName
                                          ]?.[cabType._id] ||
                                          0
                                        }
                                        onChange={(e) =>
                                          handleCitySpecificPriceChange(
                                            cityName,
                                            cabType.name,
                                            e.target.value,
                                          )
                                        }
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="0"
                                        min="0"
                                      />
                                    </div>
                                  </div>

                                  {/* Extra Fare Per Hour */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Extra Fare/Hour (₹)
                                    </label>
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-gray-600">
                                        ���
                                      </span>
                                      <input
                                        type="number"
                                        value={
                                          formData.citySpecificExtraFarePerHour[
                                            cityName
                                          ]?.[cabType.name] ||
                                          formData.citySpecificExtraFarePerHour[
                                            cityName
                                          ]?.[cabType._id] ||
                                          0
                                        }
                                        onChange={(e) =>
                                          handleCitySpecificExtraFarePerHourChange(
                                            cityName,
                                            cabType.name,
                                            e.target.value,
                                          )
                                        }
                                        className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-green-500 focus:border-transparent"
                                        placeholder="0"
                                        min="0"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        <FaRoute className="mx-auto text-gray-400 text-2xl mb-2" />
                        <p className="text-sm">
                          Add service cities first to set cab prices for each
                          city
                        </p>
                      </div>
                    )}
                    {cabTypes.length === 0 && (
                      <div className="text-center text-gray-500 py-4">
                        No cab types available
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {editingPackage ? "Update" : "Create"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalPackages;
